import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import getConfig from 'next/config';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { PageEventObject } from 'src/tracking/events/pageEvent';
import { buildPageEventObject } from 'src/tracking/events/pageEvent';
import { getFilterNavType } from 'src/tracking/utils/getFilterNavType';
import type { NextConfig } from 'src/types/nextConfig';
import { logger } from 'src/utils/serverLogger';

/**
 * Event name GTM triggers on for a soft navigation between listings.
 *
 * GTM does not fire a page view on History Change, so this name is what the
 * container matches on: its trigger re-fires the GA configuration tag, which
 * re-reads the refreshed `page` object below and applies it to every subsequent
 * event, and both Google Retail Search tags. Named for the PLP surface rather
 * than the taxonomy template because that is the name agreed with analytics and
 * built into the GTM container. See DISC-4554.
 */
export const PLP_VIRTUAL_PAGE_VIEW_EVENT = 'plp_virtual_page_view';

export type VirtualPageViewEvent = PageEventObject & {
  event: typeof PLP_VIRTUAL_PAGE_VIEW_EVENT;
};

/**
 * The page keys `buildPageEventObject` only emits when it has a value for them.
 *
 * GTM merges each data layer push into its model recursively, so a key we leave
 * out is not cleared, it keeps the value from the page the customer just left.
 * Navigating up to `/shop/all`, which has no active taxonomy level and so no
 * `crs_categories`, would otherwise leave the previous listing's breadcrumb on
 * every subsequent Vertex event, which is the exact staleness this event exists
 * to fix.
 *
 * Applied to the client push only. The server's push is the first of the page
 * load and has nothing to clear.
 *
 * `exact_listing_items` and `expanded_listing_items` are not here: they are
 * expanded-search fields that only appear on the search template, which no
 * taxonomy soft navigation can reach.
 */
const CLEARED_OPTIONAL_PAGE_KEYS: Required<
  Pick<
    PageEventObject['page'],
    'attribution_token' | 'crs_categories' | 'crs_filter' | 'returned_products'
  >
> = {
  attribution_token: '',
  crs_categories: '',
  crs_filter: '',
  returned_products: [],
};

/**
 * Refreshes the data layer `page` object after a soft navigation to a different
 * taxonomy listing.
 *
 * The server-rendered object is written once by an inline script and cannot
 * re-execute, so without this every event for the rest of the session carries
 * the entry page's `list_id`, `total_listing_items`, `attribution_token` and
 * `returned_products`. See DISC-4541.
 *
 * Not deferred, unlike the filter events. This has to reach the data layer
 * before any impression for the newly rendered products, or those impressions
 * are attributed to the page the customer just left. Calling it from a Redux
 * event handler means the products are in state but React has not re-rendered
 * yet, which is exactly the window we want.
 */
export const sendTaxonomyPageViewEvent = (state: ReduxApplicationState) => {
  const { publicRuntimeConfig } = getConfig() as NextConfig;

  let pageEventObject: PageEventObject;

  try {
    pageEventObject = buildPageEventObject(
      publicRuntimeConfig.version,
      publicRuntimeConfig.appVersion,
      state,
      { filterNavType: getFilterNavType() },
    );
  } catch (error) {
    // A page object we cannot build is worse than none: it would overwrite the
    // entry page's values with blanks. Leave the data layer as it is.
    logger.warn({
      location: 'sendTaxonomyPageViewEvent',
      extra: { error },
    });

    return;
  }

  sendGAEvent<VirtualPageViewEvent>({
    event: PLP_VIRTUAL_PAGE_VIEW_EVENT,
    ...pageEventObject,
    page: { ...CLEARED_OPTIONAL_PAGE_KEYS, ...pageEventObject.page },
  });
};
